import React, { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished } from '../utils/constants';
import { isPotentialOpener, getPreviousProbablePitcher } from '../utils/mlbBullpen';
import SplitsView from './SplitsView';
import DesktopTablesView from './DesktopTablesView';
import MobileAnalyticsDataTable from './MobileAnalyticsDataTable';
import PlayerModal from './PlayerModal';

// Helper: renders "vs. {name}[*][ (hand)]" + a line beneath with the
// previous probable pitcher when the current probable is a potential opener.
// Numeric prefixes (₁ opener, ₂ true starter) render as HTML <sub>.
const renderMlbPitcherHeader = (game, isHomeSide) => {
  const pitcher = isHomeSide ? game?.homeProbablePitcher : game?.awayProbablePitcher;
  if (!pitcher) return 'vs. TBD';
  const opener = isPotentialOpener(game, isHomeSide);
  const prev = opener ? getPreviousProbablePitcher(game, isHomeSide) : null;
  const throws = pitcher.throws ? ` (${pitcher.throws})` : '';
  return (
    <>
      vs. {opener && <sup>1</sup>}{pitcher.fullName || 'TBD'}{opener ? '*' : ''}{throws}
      {prev && (
        <div style={{ opacity: 0.85 }}>
          <sup>2</sup>{prev.fullName}{prev.throws ? ` (${prev.throws})` : ''}
        </div>
      )}
    </>
  );
};

const AnalyticsDataTable = ({
  analyticsByGameId,
  analyticsLoadingGameIds,
  gameTime,
  gameStatus,
  gameDate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  isMobile,
  isCollapsed = false,
  onCollapseToggle
}) => {
  // Local state for analytics type and year selection
  const [analyticsType, setAnalyticsType] = useState('matchups'); // 'matchups', 'performance', or 'pitches'
  const [selectedYear, setSelectedYear] = useState('alltime');
  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  
  // Performance view state - 'all', 'last7', 'last14', 'last30'
  const [performanceView, setPerformanceView] = useState('all');
  // Pitcher hand filter - 'vsAll', 'vsRHP', 'vsLHP'
  const [pitcherHandFilter, setPitcherHandFilter] = useState('vsAll');
  // Pitches view state - 'main', 'advanced'
  const [pitchesView, setPitchesView] = useState('main');
  // Pitches display mode - 'all' or 'splits'
  const [pitchesDisplayMode, setPitchesDisplayMode] = useState('all');
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = game || { away: 'TBD', home: 'TBD', gameIDmlb: 'loading' };

  // Player modal state
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [playerModalData, setPlayerModalData] = useState({
    gameId: null,
    playerId: null,
    playerName: null,
    market: null,
    awayTeam: null,
    homeTeam: null
  });


  // Handle player click - open player modal
  const handlePlayerClick = (gameId, playerId, playerName, market, awayTeam, homeTeam) => {
    console.log('AnalyticsDataTable - handlePlayerClick called:', {
      gameId,
      playerId,
      playerName,
      market,
      awayTeam,
      homeTeam
    });

    setPlayerModalData({
      gameId,
      playerId,
      playerName,
      market,
      awayTeam,
      homeTeam
    });
    setShowPlayerModal(true);

    console.log('AnalyticsDataTable - Modal state updated:', {
      showPlayerModal: true,
      playerModalData: {
        gameId,
        playerId,
        playerName,
        market,
        awayTeam,
        homeTeam
      }
    });
  };

  // Handle player modal close
  const handleClosePlayerModal = () => {
    setShowPlayerModal(false);
    setPlayerModalData({
      gameId: null,
      playerId: null,
      playerName: null,
      market: null,
      awayTeam: null,
      homeTeam: null
    });
  };

  // Fetch analytics data when analytics type changes
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!safeGame.gameIDmlb || safeGame.gameIDmlb === 'loading') return;
      
      // Check if we already have this data
      const existingData = analyticsByGameId[safeGame.gameIDmlb]?.[analyticsType];
      if (existingData) {
        setLocalData(existingData);
        return;
      }
      
      setLocalLoading(true);
      try {
        const endpoint = `/api/analytics/game/${safeGame.gameIDmlb}/${analyticsType === 'pitches' ? 'pitches-all' : analyticsType}`;
        const response = await fetch(endpoint);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Process the data based on analytics type
        let processedData = [];
        
        if (analyticsType === 'matchups') {
          // Handle year-based matchup data
          const battersArray = data.matchups || [];
          const yearlyData = { alltime: [], '2024': [], '2025': [] };
          
          if (Array.isArray(battersArray)) {
            battersArray.forEach(batter => {
              if (batter.matchups && typeof batter.matchups === 'object') {
                Object.entries(batter.matchups).forEach(([pitcherId, pitcherData]) => {
                  if (pitcherData.matchup && typeof pitcherData.matchup === 'object') {
                    Object.entries(pitcherData.matchup).forEach(([year, yearData]) => {
                      if (yearlyData[year]) {
                        yearlyData[year].push({
                          batterId: batter.batterId,
                          batterName: batter.batterName,
                          batterTeam: batter.batterTeam,
                          batterBats: batter.batterBats,
                          pitcherId: pitcherId,
                          pitcherName: pitcherData.pitcherName,
                          pitcherTeam: pitcherData.pitcherTeam,
                          pitcherThrows: pitcherData.pitcherThrows,
                          totalAtBats: yearData.atBats || 0,
                          hits: yearData.hits || 0,
                          homers: yearData.homeRuns || 0,
                          strikeouts: yearData.strikeouts || 0,
                          walks: yearData.walks || 0,
                          battingAvg: yearData.battingAvg || 0,
                          obp: yearData.onBasePct || 0,
                          slg: yearData.sluggingPct || 0,
                          ops: yearData.ops || 0
                        });
                      }
                    });
                  }
                });
              }
            });
          }
          setLocalData(yearlyData);
        } else if (analyticsType === 'pitches') {
          // Store raw pitches data - processing will happen in separate useEffect
          console.log('=== RECEIVED PITCHES DATA FROM ENDPOINT ===');
          console.log('Raw data structure:', {
            pitchersCount: data.pitchers?.length || 0,
            battersCount: data.batters?.length || 0,
            firstPitcher: data.pitchers?.[0] ? {
              name: data.pitchers[0].pitcherName,
              team: data.pitchers[0].pitcherTeam,
              pitchTypes: data.pitchers[0].top4Pitches?.vsAll?.map(p => p.pitchType)
            } : null,
            firstBatter: data.batters?.[0] ? {
              name: data.batters[0].batterName,
              team: data.batters[0].batterTeam,
              vsAllPitches: data.batters[0].allPitches?.vsAll?.map(p => ({
                type: p.pitchType,
                avg: p.battingAvg
              }))
            } : null
          });
          
          // Create splits data structure for easier access
          const createSplitsData = () => {
            const pitchers = data.pitchers || [];
            const batters = data.batters || [];
            const splitsData = {};
            
            // Get home and away pitchers
            const homePitcher = pitchers.find(p => p.pitcherTeam === safeGame.home);
            const awayPitcher = pitchers.find(p => p.pitcherTeam === safeGame.away);
            
            console.log('=== CREATING SPLITS DATA ===');
            console.log('Home pitcher:', homePitcher?.pitcherName, 'Throws:', homePitcher?.pitcherThrows);
            console.log('Away pitcher:', awayPitcher?.pitcherName, 'Throws:', awayPitcher?.pitcherThrows);
            
            // Process batters with proper split stats based on opposing pitcher
            const processBattersForSplits = (batterTeam, opposingPitcher) => {
              const teamBatters = batters.filter(b => b.batterTeam === batterTeam);
              const pitcherHand = opposingPitcher?.pitcherThrows === 'Left' || opposingPitcher?.pitcherThrows === 'L' ? 'vsLHP' : 'vsRHP';
              
              console.log(`Processing ${batterTeam} batters vs ${pitcherHand}:`);
              console.log('First batter data:', teamBatters[0] ? {
                name: teamBatters[0].batterName,
                hasTop4Pitches: !!teamBatters[0].allPitches,
                vsLHP: teamBatters[0].allPitches?.vsLHP?.length,
                vsRHP: teamBatters[0].allPitches?.vsRHP?.length,
                vsAll: teamBatters[0].allPitches?.vsAll?.length
              } : 'No batters');
              
              return teamBatters.map(batter => ({
                ...batter,
                splitStats: batter.allPitches?.[pitcherHand] || [],
                pitcherHandFacing: pitcherHand
              }));
            };
            
            // Away batters face home pitcher
            splitsData.awayBattersWithSplits = processBattersForSplits(safeGame.away, homePitcher);
            // Home batters face away pitcher  
            splitsData.homeBattersWithSplits = processBattersForSplits(safeGame.home, awayPitcher);
            
            console.log('Splits data created:', {
              awayBattersCount: splitsData.awayBattersWithSplits.length,
              homeBattersCount: splitsData.homeBattersWithSplits.length,
              firstAwayBatterSplitStats: splitsData.awayBattersWithSplits[0]?.splitStats
            });
            
            return splitsData;
          };
          
          setLocalData({
            rawPitchersData: data.pitchers || [],
            rawBattersData: data.batters || [],
            splitsData: createSplitsData()
          });
          
        } else if (analyticsType === 'performance') {
          // Handle performance data
          const battersArray = data.performance || data;
          const performanceData = [];
          
          if (Array.isArray(battersArray)) {
            battersArray.forEach((batter, i) => {
              const last7 = batter.performance?.last7Days?.[pitcherHandFilter] || {};
              const last14 = batter.performance?.last14Days?.[pitcherHandFilter] || {};
              const last30 = batter.performance?.last30Days?.[pitcherHandFilter] || {};

              // Debug logging for HH and BRL data
              if (batter.batterName === 'Francisco Lindor' || i === 0) {
                console.log('Performance Debug for', batter.batterName, {
                  pitcherHandFilter,
                  last7,
                  hardHitRate7: last7.hardHitRate,
                  barrelRate7: last7.barrelRate,
                  hardHitRate14: last14.hardHitRate,
                  barrelRate14: last14.barrelRate,
                  hardHitRate30: last30.hardHitRate,
                  barrelRate30: last30.barrelRate
                });
              }

              performanceData.push({
                batterId: batter.batterId,
                batterName: batter.batterName,
                batterTeam: batter.batterTeam,
                batterBats: batter.batterBats,
                pitcherId: 'N/A',
                pitcherName: 'All Pitchers',
                pitcherThrows: 'N/A',
                // Last 7 days data
                last7_plateAppearances: last7.plateAppearances || 0,
                last7_totalAtBats: last7.atBats || 0,
                last7_hits: last7.hits || 0,
                last7_doubles: last7.doubles || 0,
                last7_triples: last7.triples || 0,
                last7_homers: last7.homers || 0,
                last7_strikeouts: last7.strikeouts || 0,
                last7_walks: last7.walks || 0,
                last7_battingAvg: last7.battingAvg || 0,
                last7_hardHits: last7.hardHitRate || 0,
                last7_slg: last7.sluggingPct || 0,
                last7_barrels: last7.barrelRate || 0,
                last7_strikeoutRate: last7.strikeoutRate || 0,
                last7_walkRate: last7.walkRate || 0,
                // Last 14 days data
                last14_plateAppearances: last14.plateAppearances || 0,
                last14_totalAtBats: last14.atBats || 0,
                last14_hits: last14.hits || 0,
                last14_doubles: last14.doubles || 0,
                last14_triples: last14.triples || 0,
                last14_homers: last14.homers || 0,
                last14_strikeouts: last14.strikeouts || 0,
                last14_walks: last14.walks || 0,
                last14_battingAvg: last14.battingAvg || 0,
                last14_hardHits: last14.hardHitRate || 0,
                last14_slg: last14.sluggingPct || 0,
                last14_barrels: last14.barrelRate || 0,
                last14_strikeoutRate: last14.strikeoutRate || 0,
                last14_walkRate: last14.walkRate || 0,
                // Last 30 days data
                last30_plateAppearances: last30.plateAppearances || 0,
                last30_totalAtBats: last30.atBats || 0,
                last30_hits: last30.hits || 0,
                last30_doubles: last30.doubles || 0,
                last30_triples: last30.triples || 0,
                last30_homers: last30.homers || 0,
                last30_strikeouts: last30.strikeouts || 0,
                last30_walks: last30.walks || 0,
                last30_battingAvg: last30.battingAvg || 0,
                last30_hardHits: last30.hardHitRate || 0,
                last30_slg: last30.sluggingPct || 0,
                last30_barrels: last30.barrelRate || 0,
                last30_strikeoutRate: last30.strikeoutRate || 0,
                last30_walkRate: last30.walkRate || 0
              });
            });
          }
          setLocalData(performanceData);
        }
      } catch (error) {
        console.error(`Failed to fetch ${analyticsType} data:`, error);
        setLocalData([]);
      } finally {
        setLocalLoading(false);
      }
    };
    
    fetchAnalyticsData();
  }, [analyticsType, safeGame.gameIDmlb, analyticsByGameId, pitcherHandFilter]);
  
  // Define the stat columns based on analytics type
  const matchupsStatColumns = [
    { key: 'totalAtBats', label: 'AB', name: 'At Bats' },
    { key: 'hits', label: 'H', name: 'Hits' },
    { key: 'homers', label: 'HR', name: 'Home Runs' },
    { key: 'strikeouts', label: 'K', name: 'Strikeouts' },
    { key: 'walks', label: 'BB', name: 'Walks' },
    { key: 'battingAvg', label: 'AVG', name: 'Batting Average', format: 'decimal' },
    { key: 'obp', label: 'OBP', name: 'On Base Percentage', format: 'decimal' },
    { key: 'slg', label: 'SLG', name: 'Slugging Percentage', format: 'decimal' },
    { key: 'ops', label: 'OPS', name: 'On Base Plus Slugging', format: 'decimal' }
  ];
  
  const getPitcherSpecificColumns = (pitcherData, splitType = null) => {
    const columns = [];
    
    // For splits mode, use split-specific pitch types
    if (splitType && pitcherData?.splits?.[splitType]) {
      const splitPitches = pitcherData.splits[splitType];
      const pitchTypes = ['pitch1', 'pitch2', 'pitch3', 'pitch4'];
      
      pitchTypes.forEach((pitchType, index) => {
        const pitch = splitPitches[index];
        if (pitch) {
          let pitchName = pitch.pitchType || `Pitch ${index + 1}`;
          
          // Abbreviate Fastball to FB
          if (pitchName.toLowerCase().includes('fastball')) {
            pitchName = pitchName.replace(/Four-Seam Fastball/i, 'Four-Seam FB')
                               .replace(/Two-Seam Fastball/i, 'Two-Seam FB')
                               .replace(/Fastball/i, 'FB');
          }
          
          const groupLabel = `${pitchName} (${pitch.usage || '0'}%)`;
          
          if (pitchesView === 'main') {
            columns.push(
              { key: `${pitchType}_avg`, label: 'Avg', name: `${pitchName} Average`, format: 'decimal', group: groupLabel },
              { key: `${pitchType}_slg`, label: 'SLG', name: `${pitchName} Slugging`, format: 'decimal', group: groupLabel },
              { key: `${pitchType}_hr`, label: 'HR', name: `${pitchName} Home Runs`, group: groupLabel }
            );
          } else {
            columns.push(
              { key: `${pitchType}_hhPercent`, label: 'hh%', name: `${pitchName} Hard Hit Percent`, format: 'percentage', group: groupLabel },
              { key: `${pitchType}_barrelPercent`, label: 'barrel%', name: `${pitchName} Barrel Percent`, format: 'percentage', group: groupLabel },
              { key: `${pitchType}_avgXV`, label: 'avgXV', name: `${pitchName} Average Exit Velocity`, format: 'velocity', group: groupLabel }
            );
          }
          
          // Add separator after each pitch group except the last
          if (index < 3 && splitPitches[index + 1]) {
            columns.push({ key: `separator${index}`, label: '', name: '', isSeparator: true });
          }
        }
      });
      
      return columns;
    }
    
    // Original logic for non-splits mode
    if (!pitcherData || !pitcherData.topPitchTypes) {
      // Return default columns if no pitcher data
      return getPitchesColumns();
    }
    
    const pitchTypes = ['pitch1', 'pitch2', 'pitch3', 'pitch4'];
    
    pitchTypes.forEach((pitchType, index) => {
      const actualPitchType = pitcherData.topPitchTypes[index];
      const pitchInfo = pitcherData.pitchTypeCounts?.[actualPitchType];
      
      if (actualPitchType && pitchInfo) {
        let pitchName = pitchInfo.name || actualPitchType;
        
        // Abbreviate Fastball to FB
        if (pitchName.toLowerCase().includes('fastball')) {
          pitchName = pitchName.replace(/Four-Seam Fastball/i, 'Four-Seam FB')
                             .replace(/Two-Seam Fastball/i, 'Two-Seam FB')
                             .replace(/Fastball/i, 'FB');
        }
        
        const groupLabel = `${pitchName} (${pitchInfo.percentage}%)`;
        
        if (pitchesView === 'main') {
          columns.push(
            { key: `${pitchType}_avg`, label: 'Avg', name: `${pitchName} Average`, format: 'decimal', group: groupLabel },
            { key: `${pitchType}_slg`, label: 'SLG', name: `${pitchName} Slugging`, format: 'decimal', group: groupLabel },
            { key: `${pitchType}_hr`, label: 'HR', name: `${pitchName} Home Runs`, group: groupLabel }
          );
        } else {
          columns.push(
            { key: `${pitchType}_hhPercent`, label: 'hh%', name: `${pitchName} Hard Hit Percent`, format: 'percentage', group: groupLabel },
            { key: `${pitchType}_barrelPercent`, label: 'barrel%', name: `${pitchName} Barrel Percent`, format: 'percentage', group: groupLabel },
            { key: `${pitchType}_avgXV`, label: 'avgXV', name: `${pitchName} Average Exit Velocity`, format: 'velocity', group: groupLabel }
          );
        }
        
        // Add separator between pitch types (except after last one)
        if (index < 3 && pitcherData.topPitchTypes[index + 1]) {
          columns.push({ key: `separator_${index}`, label: '', name: '', isSeparator: true });
        }
      }
    });
    
    return columns;
  };
  
  const getPitchesColumns = () => {
    // Get the top pitch types from local data if available
    const topPitchTypes = localData?.topPitchTypes || [];
    const pitchTypeCounts = localData?.pitchTypeCounts || {};
    
    const pitchTypes = ['pitch1', 'pitch2', 'pitch3', 'pitch4'];
    const columns = [];
    
    pitchTypes.forEach((pitchType, index) => {
      const pitchNum = index + 1;
      const actualPitchType = topPitchTypes[index];
      let pitchName = pitchTypeCounts[actualPitchType]?.name || actualPitchType || `Pitch ${pitchNum}`;
      
      // Abbreviate Fastball to FB
      if (pitchName.toLowerCase().includes('fastball')) {
        pitchName = pitchName.replace(/Four-Seam Fastball/i, 'Four-Seam FB')
                           .replace(/Two-Seam Fastball/i, 'Two-Seam FB')
                           .replace(/Fastball/i, 'FB');
      }
      
      const pitchPercentage = pitchTypeCounts[actualPitchType]?.percentage || '0.0';
      const groupLabel = actualPitchType ? `${pitchName} (${pitchPercentage}%)` : `Pitch ${pitchNum} (#)`;
      
      if (pitchesView === 'main') {
        columns.push(
          { key: `${pitchType}_avg`, label: 'Avg', name: `${pitchName} Average`, format: 'decimal', group: groupLabel },
          { key: `${pitchType}_slg`, label: 'SLG', name: `${pitchName} Slugging`, format: 'decimal', group: groupLabel },
          { key: `${pitchType}_hr`, label: 'HR', name: `${pitchName} Home Runs`, group: groupLabel }
        );
      } else {
        columns.push(
          { key: `${pitchType}_hhPercent`, label: 'hh%', name: `${pitchName} Hard Hit Percent`, format: 'percentage', group: groupLabel },
          { key: `${pitchType}_barrelPercent`, label: 'barrel%', name: `${pitchName} Barrel Percent`, format: 'percentage', group: groupLabel },
          { key: `${pitchType}_avgXV`, label: 'avgXV', name: `${pitchName} Average Exit Velocity`, format: 'velocity', group: groupLabel }
        );
      }
      
      // Add separator between pitch types (except after last one)
      if (index < pitchTypes.length - 1) {
        columns.push({ key: `separator_${index}`, label: '', name: '', isSeparator: true });
      }
    });
    
    return columns;
  };

  // Store pitcher-specific columns for each team
  const [awayTeamPitchColumns, setAwayTeamPitchColumns] = useState([]);
  const [homeTeamPitchColumns, setHomeTeamPitchColumns] = useState([]);
  
  // Store pitcher data to regenerate columns when pitchesView changes
  const [storedHomePitcher, setStoredHomePitcher] = useState(null);
  const [storedAwayPitcher, setStoredAwayPitcher] = useState(null);
  
  // Only recalculate pitches columns when analyticsType is 'pitches'
  const pitchesStatColumns = analyticsType === 'pitches' ? getPitchesColumns() : [];
  
  // Update team-specific columns when pitchesView changes
  useEffect(() => {
    if (analyticsType === 'pitches') {
      if (storedHomePitcher) {
        setAwayTeamPitchColumns(getPitcherSpecificColumns(storedHomePitcher));
      }
      if (storedAwayPitcher) {
        setHomeTeamPitchColumns(getPitcherSpecificColumns(storedAwayPitcher));
      }
    }
  }, [pitchesView, analyticsType, storedHomePitcher, storedAwayPitcher]);

  // Process pitches data when pitcherHandFilter changes
  useEffect(() => {
    if (analyticsType === 'pitches' && localData?.rawPitchersData && localData?.rawBattersData) {
      const processPitchesData = () => {
        const battersArray = localData.rawBattersData;
        const pitchersArray = localData.rawPitchersData;
        let pitchesData = [];
        
        // Process pitchers - create rows based on display mode
        pitchersArray.forEach(pitcher => {
          const throwingHandDisplay = pitcher.pitcherThrows ? ` (${pitcher.pitcherThrows.charAt(0)})` : '';
          
          // For splits mode, show vsRHB and vsLHB; for all mode, show vsAll
          const handTypes = pitchesDisplayMode === 'splits' ? ['vsRHB', 'vsLHB'] : ['vsAll'];
          handTypes.forEach(handType => {
            const pitcherRow = {
              playerId: `pitcher_${pitcher.pitcherId}_${handType}`,
              playerName: handType === 'vsAll' ? 
                pitcher.pitcherName + throwingHandDisplay : 
                `${pitcher.pitcherName} pitches vs ${handType.replace('vs', '')}`,
              batterName: handType === 'vsAll' ? 
                pitcher.pitcherName + throwingHandDisplay : 
                `${pitcher.pitcherName} stats vs${handType.replace('vs', 'vs ')}`,
              batterTeam: pitcher.pitcherTeam,
              team: pitcher.pitcherTeam,
              teamLogo: pitcher.teamLogo,
              throwingHand: pitcher.pitcherThrows,
              isPitcher: true,
              handType,
              pitcherSplitType: handType, // Store the split type for column generation
              pitchesForSplit: pitcher.top4Pitches?.[handType] || [] // Store the actual pitches for this split
            };
            
            // Get pitch data for this specific handType
            const pitchesForHandType = pitcher.top4Pitches?.[handType] || [];
            if (Array.isArray(pitchesForHandType)) {
              pitchesForHandType.forEach((pitch, index) => {
                const pitchNum = index + 1;
                pitcherRow[`pitch${pitchNum}_percent`] = pitch.usage || 0;
                pitcherRow[`pitch${pitchNum}_type`] = pitch.pitchType;
                pitcherRow[`pitch${pitchNum}_name`] = pitch.pitchType;
                
                // Add pitcher stats for this pitch type
                pitcherRow[`pitch${pitchNum}_avg`] = pitch.battingAvg || 0;
                pitcherRow[`pitch${pitchNum}_slg`] = pitch.sluggingPct || 0;
                pitcherRow[`pitch${pitchNum}_hr`] = pitch.homeRuns || 0;
                pitcherRow[`pitch${pitchNum}_hhPercent`] = pitch.hardHitRate || 0;
                pitcherRow[`pitch${pitchNum}_barrelPercent`] = pitch.barrelRate || 0;
                pitcherRow[`pitch${pitchNum}_avgXV`] = pitch.avgExitVelo || 0;
              });
            }
            
            pitchesData.push(pitcherRow);
          });
        });
        
        // Extract top pitch types from server data for column headers (do this first)
        const topPitchTypes = [];
        const pitchTypeCounts = {};
        
        // In splits mode, we need to track pitch types for each split separately
        const splitsPitchTypes = {
          home: { vsRHB: [], vsLHB: [] },
          away: { vsRHB: [], vsLHB: [] }
        };
        
        if (pitchersArray.length > 0) {
          // For default mode, use first pitcher's vsAll
          if (pitchesDisplayMode !== 'splits') {
            const firstPitcher = pitchersArray[0];
            const vsAllPitches = firstPitcher.top4Pitches?.vsAll || [];
            vsAllPitches.forEach((pitch, index) => {
              topPitchTypes[index] = pitch.pitchType;
              pitchTypeCounts[pitch.pitchType] = {
                name: pitch.pitchType,
                percentage: pitch.usage,
                type: pitch.pitchType
              };
            });
          } else {
            // For splits mode, get pitch types for each pitcher and split
            const homePitcher = pitchersArray.find(p => p.pitcherTeam === safeGame.home);
            const awayPitcher = pitchersArray.find(p => p.pitcherTeam === safeGame.away);
            
            if (homePitcher) {
              homePitcher.top4Pitches?.vsRHB?.forEach((pitch, index) => {
                splitsPitchTypes.home.vsRHB[index] = pitch.pitchType;
              });
              homePitcher.top4Pitches?.vsLHB?.forEach((pitch, index) => {
                splitsPitchTypes.home.vsLHB[index] = pitch.pitchType;
              });
            }
            
            if (awayPitcher) {
              awayPitcher.top4Pitches?.vsRHB?.forEach((pitch, index) => {
                splitsPitchTypes.away.vsRHB[index] = pitch.pitchType;
              });
              awayPitcher.top4Pitches?.vsLHB?.forEach((pitch, index) => {
                splitsPitchTypes.away.vsLHB[index] = pitch.pitchType;
              });
            }
          }
        }
        
        // Get the correct pitcher's pitch types for each team
        const homePitcher = pitchersArray.find(p => p.pitcherTeam === safeGame.home);
        const awayPitcher = pitchersArray.find(p => p.pitcherTeam === safeGame.away);
        
        // Away batters face home pitcher, home batters face away pitcher
        const homePitcherTypes = homePitcher?.top4Pitches?.vsAll?.map(p => p.pitchType) || [];
        const awayPitcherTypes = awayPitcher?.top4Pitches?.vsAll?.map(p => p.pitchType) || [];
        
        
        // Process batters with current filter
        battersArray.forEach((batter, batterIndex) => {
          const handFilter = 'vsAll'; // Always use vsAll for default pitches view
          
          const batterRow = {
            playerId: batter.batterId,
            playerName: batter.batterName,
            batterName: batter.batterName,
            batterTeam: batter.batterTeam || batter.team,
            team: batter.batterTeam || batter.team,
            teamLogo: batter.teamLogo,
            battingHand: batter.batterBats || batter.battingHand,
            isPitcher: false,
            handType: 'batter',
            isSplitsLabel: false
          };
          
          // Determine which pitcher's pitch types to use based on batter's team and splits mode
          let pitchTypesToUse;
          if (pitchesDisplayMode === 'splits') {
            // In splits mode, use pitch types specific to batter's handedness
            const batterHand = batter.batterBats === 'Right' ? 'vsRHB' : 'vsLHB';
            if (batter.batterTeam === safeGame.away) {
              // Away batters face home pitcher
              pitchTypesToUse = splitsPitchTypes.home[batterHand] || [];
            } else {
              // Home batters face away pitcher
              pitchTypesToUse = splitsPitchTypes.away[batterHand] || [];
            }
          } else {
            // Default mode - use vsAll pitch types
            pitchTypesToUse = batter.batterTeam === safeGame.away ? homePitcherTypes : awayPitcherTypes;
          }
          
          
          // Add pitch performance data from server
          pitchTypesToUse.forEach((pitchType, index) => {
            const pitchNum = index + 1;
            const batterPitchesArray = batter.allPitches?.[handFilter] || [];
            const pitchData = batterPitchesArray.find(p => p.pitchType === pitchType) || {};
            
            
            batterRow[`pitch${pitchNum}_avg`] = pitchData?.battingAvg || 0;
            batterRow[`pitch${pitchNum}_slg`] = pitchData?.sluggingPct || 0;
            batterRow[`pitch${pitchNum}_hr`] = pitchData?.homeRuns || 0;
            batterRow[`pitch${pitchNum}_hhPercent`] = pitchData?.hardHitRate || 0;
            batterRow[`pitch${pitchNum}_barrelPercent`] = pitchData?.barrelRate || 0;
            batterRow[`pitch${pitchNum}_avgXV`] = pitchData?.avgExitVelo || 0;
            batterRow[`pitch${pitchNum}_type`] = pitchType;
            batterRow[`pitch${pitchNum}_name`] = pitchType;
          });
          
          pitchesData.push(batterRow);
        });
        
        
        // Set team-specific pitcher data for columns
        // Away team sees home pitcher, home team sees away pitcher
        // Note: homePitcher and awayPitcher already declared above
        
        if (homePitcher && homePitcher.top4Pitches) {
          const homePitcherData = {
            topPitchTypes: homePitcher.top4Pitches.vsAll?.map(p => p.pitchType) || [],
            pitchTypeCounts: {},
            splits: {
              vsRHB: homePitcher.top4Pitches.vsRHB || [],
              vsLHB: homePitcher.top4Pitches.vsLHB || []
            }
          };
          
          // Store vsAll pitch data
          homePitcher.top4Pitches.vsAll?.forEach(pitch => {
            homePitcherData.pitchTypeCounts[pitch.pitchType] = {
              name: pitch.pitchType,
              percentage: pitch.usage,
              type: pitch.pitchType
            };
          });
          
          setStoredHomePitcher(homePitcherData);
          // Columns will be set differently based on display mode
          if (pitchesDisplayMode !== 'splits') {
            setAwayTeamPitchColumns(getPitcherSpecificColumns(homePitcherData));
          }
        }
        
        if (awayPitcher && awayPitcher.top4Pitches) {
          const awayPitcherData = {
            topPitchTypes: awayPitcher.top4Pitches.vsAll?.map(p => p.pitchType) || [],
            pitchTypeCounts: {},
            splits: {
              vsRHB: awayPitcher.top4Pitches.vsRHB || [],
              vsLHB: awayPitcher.top4Pitches.vsLHB || []
            }
          };
          
          // Store vsAll pitch data
          awayPitcher.top4Pitches.vsAll?.forEach(pitch => {
            awayPitcherData.pitchTypeCounts[pitch.pitchType] = {
              name: pitch.pitchType,
              percentage: pitch.usage,
              type: pitch.pitchType
            };
          });
          
          setStoredAwayPitcher(awayPitcherData);
          // Columns will be set differently based on display mode
          if (pitchesDisplayMode !== 'splits') {
            setHomeTeamPitchColumns(getPitcherSpecificColumns(awayPitcherData));
          }
        }
        
        // Store the data with splits info
        setLocalData({
          batters: pitchesData.filter(row => !row.isPitcher),
          pitchers: pitchesData.filter(row => row.isPitcher),
          topPitchTypes,
          pitchTypeCounts,
          splitsPitchTypes: pitchesDisplayMode === 'splits' ? splitsPitchTypes : null,
          rawPitchersData: pitchersArray,
          rawBattersData: battersArray,
          // Keep the splits data that was created on initial load
          splitsData: localData?.splitsData
        });
      };
      
      processPitchesData();
    }
  }, [analyticsType, pitchesDisplayMode, localData?.rawPitchersData, localData?.rawBattersData]);

  // Define columns based on performance view selection
  const getPerformanceColumns = () => {
    if (performanceView === 'all') {
      // Show AB, percentage values, and OBP for all 3 periods with separators
      return [
        // Last 7 stats
        { key: 'last7_totalAtBats', label: 'AB', name: 'At Bats (Last 7)', group: 'Last 7' },
        { key: 'last7_battingAvg', label: 'AVG', name: 'Batting Average (Last 7)', format: 'decimal', group: 'Last 7' },
        { key: 'last7_slg', label: 'SLG', name: 'Slugging Percentage (Last 7)', format: 'decimal', group: 'Last 7' },
        { key: 'last7_hardHits', label: 'HH', name: 'Hard Hits (Last 7)', format: 'percentage', group: 'Last 7' },
        { key: 'last7_barrels', label: 'BRL', name: 'Barrels (Last 7)', format: 'percentage', group: 'Last 7' },
        // Separator
        { key: 'separator1', label: '', name: '', isSeparator: true },
        // Last 14 stats
        { key: 'last14_totalAtBats', label: 'AB', name: 'At Bats (Last 14)', group: 'Last 14' },
        { key: 'last14_battingAvg', label: 'AVG', name: 'Batting Average (Last 14)', format: 'decimal', group: 'Last 14' },
        { key: 'last14_slg', label: 'SLG', name: 'Slugging Percentage (Last 14)', format: 'decimal', group: 'Last 14' },
        { key: 'last14_hardHits', label: 'HH', name: 'Hard Hits (Last 14)', format: 'percentage', group: 'Last 14' },
        { key: 'last14_barrels', label: 'BRL', name: 'Barrels (Last 14)', format: 'percentage', group: 'Last 14' },
        // Separator
        { key: 'separator2', label: '', name: '', isSeparator: true },
        // Last 30 stats
        { key: 'last30_totalAtBats', label: 'AB', name: 'At Bats (Last 30)', group: 'Last 30' },
        { key: 'last30_battingAvg', label: 'AVG', name: 'Batting Average (Last 30)', format: 'decimal', group: 'Last 30' },
        { key: 'last30_slg', label: 'SLG', name: 'Slugging Percentage (Last 30)', format: 'decimal', group: 'Last 30' },
        { key: 'last30_hardHits', label: 'HH', name: 'Hard Hits (Last 30)', format: 'percentage', group: 'Last 30' },
        { key: 'last30_barrels', label: 'BRL', name: 'Barrels (Last 30)', format: 'percentage', group: 'Last 30' }
      ];
    } else if (performanceView === 'last7') {
      // Show all Last 7 stats
      return [
        { key: 'last7_plateAppearances', label: 'PA', name: 'Plate Appearances', group: 'Last 7 Days' },
        { key: 'last7_totalAtBats', label: 'AB', name: 'At Bats', group: 'Last 7 Days' },
        { key: 'last7_hits', label: 'H', name: 'Hits', group: 'Last 7 Days' },
        { key: 'last7_doubles', label: '2B', name: 'Doubles', group: 'Last 7 Days' },
        { key: 'last7_triples', label: '3B', name: 'Triples', group: 'Last 7 Days' },
        { key: 'last7_homers', label: 'HR', name: 'Home Runs', group: 'Last 7 Days' },
        { key: 'last7_strikeouts', label: 'K', name: 'Strikeouts', group: 'Last 7 Days' },
        { key: 'last7_walks', label: 'BB', name: 'Walks', group: 'Last 7 Days' },
        { key: 'last7_battingAvg', label: 'AVG', name: 'Batting Average', format: 'decimal', group: 'Last 7 Days' },
        { key: 'last7_slg', label: 'SLG', name: 'Slugging Percentage', format: 'decimal', group: 'Last 7 Days' },
        { key: 'last7_hardHits', label: 'HH', name: 'Hard Hits', format: 'percentage', group: 'Last 7 Days' },
        { key: 'last7_barrels', label: 'BRL', name: 'Barrels', format: 'percentage', group: 'Last 7 Days' },
        { key: 'last7_strikeoutRate', label: 'K%', name: 'Strikeout Rate', format: 'percentage', group: 'Last 7 Days' },
        { key: 'last7_walkRate', label: 'BB%', name: 'Walk Rate', format: 'percentage', group: 'Last 7 Days' }
      ];
    } else if (performanceView === 'last14') {
      // Show all Last 14 stats
      return [
        { key: 'last14_plateAppearances', label: 'PA', name: 'Plate Appearances', group: 'Last 14 Days' },
        { key: 'last14_totalAtBats', label: 'AB', name: 'At Bats', group: 'Last 14 Days' },
        { key: 'last14_hits', label: 'H', name: 'Hits', group: 'Last 14 Days' },
        { key: 'last14_doubles', label: '2B', name: 'Doubles', group: 'Last 14 Days' },
        { key: 'last14_triples', label: '3B', name: 'Triples', group: 'Last 14 Days' },
        { key: 'last14_homers', label: 'HR', name: 'Home Runs', group: 'Last 14 Days' },
        { key: 'last14_strikeouts', label: 'K', name: 'Strikeouts', group: 'Last 14 Days' },
        { key: 'last14_walks', label: 'BB', name: 'Walks', group: 'Last 14 Days' },
        { key: 'last14_battingAvg', label: 'AVG', name: 'Batting Average', format: 'decimal', group: 'Last 14 Days' },
        { key: 'last14_slg', label: 'SLG', name: 'Slugging Percentage', format: 'decimal', group: 'Last 14 Days' },
        { key: 'last14_hardHits', label: 'HH', name: 'Hard Hits', format: 'percentage', group: 'Last 14 Days' },
        { key: 'last14_barrels', label: 'BRL', name: 'Barrels', format: 'percentage', group: 'Last 14 Days' },
        { key: 'last14_strikeoutRate', label: 'K%', name: 'Strikeout Rate', format: 'percentage', group: 'Last 14 Days' },
        { key: 'last14_walkRate', label: 'BB%', name: 'Walk Rate', format: 'percentage', group: 'Last 14 Days' }
      ];
    } else if (performanceView === 'last30') {
      // Show all Last 30 stats
      return [
        { key: 'last30_plateAppearances', label: 'PA', name: 'Plate Appearances', group: 'Last 30 Days' },
        { key: 'last30_totalAtBats', label: 'AB', name: 'At Bats', group: 'Last 30 Days' },
        { key: 'last30_hits', label: 'H', name: 'Hits', group: 'Last 30 Days' },
        { key: 'last30_doubles', label: '2B', name: 'Doubles', group: 'Last 30 Days' },
        { key: 'last30_triples', label: '3B', name: 'Triples', group: 'Last 30 Days' },
        { key: 'last30_homers', label: 'HR', name: 'Home Runs', group: 'Last 30 Days' },
        { key: 'last30_strikeouts', label: 'K', name: 'Strikeouts', group: 'Last 30 Days' },
        { key: 'last30_walks', label: 'BB', name: 'Walks', group: 'Last 30 Days' },
        { key: 'last30_battingAvg', label: 'AVG', name: 'Batting Average', format: 'decimal', group: 'Last 30 Days' },
        { key: 'last30_slg', label: 'SLG', name: 'Slugging Percentage', format: 'decimal', group: 'Last 30 Days' },
        { key: 'last30_hardHits', label: 'HH', name: 'Hard Hits', format: 'percentage', group: 'Last 30 Days' },
        { key: 'last30_barrels', label: 'BRL', name: 'Barrels', format: 'percentage', group: 'Last 30 Days' },
        { key: 'last30_strikeoutRate', label: 'K%', name: 'Strikeout Rate', format: 'percentage', group: 'Last 30 Days' },
        { key: 'last30_walkRate', label: 'BB%', name: 'Walk Rate', format: 'percentage', group: 'Last 30 Days' }
      ];
    }
    return [];
  };

  const performanceStatColumns = getPerformanceColumns();

  const statColumns = analyticsType === 'performance' ? performanceStatColumns : 
                      analyticsType === 'pitches' ? pitchesStatColumns : matchupsStatColumns;
  
  // For pitches mode, use team-specific columns if available
  const awayTeamColumns = analyticsType === 'pitches' && awayTeamPitchColumns.length > 0 ? 
                          awayTeamPitchColumns : statColumns;
  const homeTeamColumns = analyticsType === 'pitches' && homeTeamPitchColumns.length > 0 ? 
                          homeTeamPitchColumns : statColumns;
  
  // Group columns for performance mode and pitches mode
  const getGroupedColumns = (columns) => {
    if (analyticsType !== 'performance' && analyticsType !== 'pitches') return null;
    
    const columnsToGroup = columns || statColumns;
    const groups = {};
    columnsToGroup.forEach(col => {
      if (!col.isSeparator && col.group) {
        if (!groups[col.group]) {
          groups[col.group] = [];
        }
        groups[col.group].push(col);
      }
    });
    
    return groups;
  };
  
  const groupedColumns = getGroupedColumns();
  const awayGroupedColumns = analyticsType === 'pitches' ? getGroupedColumns(awayTeamColumns) : groupedColumns;
  const homeGroupedColumns = analyticsType === 'pitches' ? getGroupedColumns(homeTeamColumns) : groupedColumns;
  
  // Mobile carousel state for stat columns
  const [mobileStatIndex, setMobileStatIndex] = useState(0);
  const statsPerPage = 3; // Reduced to 3 for better readability with more columns

  // For pitches mode, use team-specific columns for calculating pages
  const columnsForPaging = analyticsType === 'pitches' && awayTeamColumns.length > 0
    ? awayTeamColumns
    : statColumns;
  const mobileStatColumns = columnsForPaging.filter(col => !col.isSeparator);
  const totalStatPages = Math.ceil(mobileStatColumns.length / statsPerPage);
  
  // Get visible stats based on platform
  const visibleStats = isMobile 
    ? mobileStatColumns.slice(mobileStatIndex * statsPerPage, (mobileStatIndex + 1) * statsPerPage)
    : statColumns;
  
  // Team-specific visible stats for pitches mode
  const awayVisibleStats = analyticsType === 'pitches' ? 
    (isMobile ? awayTeamColumns.filter(col => !col.isSeparator).slice(mobileStatIndex * statsPerPage, (mobileStatIndex + 1) * statsPerPage) : awayTeamColumns) :
    visibleStats;
  const homeVisibleStats = analyticsType === 'pitches' ?
    (isMobile ? homeTeamColumns.filter(col => !col.isSeparator).slice(mobileStatIndex * statsPerPage, (mobileStatIndex + 1) * statsPerPage) : homeTeamColumns) :
    visibleStats;
    
  // Navigation functions for mobile stats
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const goToPrevStats = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileStatIndex(prev => (prev === 0 ? totalStatPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  const goToNextStats = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileStatIndex(prev => (prev === totalStatPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalStatPages > 1) {
        goToNextStats();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalStatPages > 1) {
        goToPrevStats();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    delta: 50
  });

  const statCellStyle = { 
    width: '70px',
    minWidth: '70px',
    maxWidth: '70px',
    textAlign: 'center',
    fontSize: '0.9rem',
    letterSpacing: '-0.025em'
  };
  
  // Calculate mobile column width based on number of stats
  const mobileStatWidth = Math.max(12, Math.floor(60 / visibleStats.length));
  const mobileNameWidth = 100 - (mobileStatWidth * visibleStats.length);

  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return '';
    
    if (timeStr === 'TBD' || timeStr === 'TBA') return timeStr;
    
    if (isGameFinished(gameStatus)) return gameStatus;
    
    if (gameStatus === 'Delayed') {
      return <Badge bg="warning" className="delayed-badge"><span className="delayed-dot"></span>Delayed</Badge>;
    }
    
    if (gameStatus === 'Postponed') {
      return <Badge bg="secondary" className="postponed-badge">Postponed</Badge>;
    }
    
    const [hour, minute] = timeStr.split(':').map(Number);
    const now = new Date();
    
    let gameDateTime = new Date();
    if (gameDate) {
      if (gameDate.includes('-')) {
        const [year, month, day] = gameDate.split('-');
        gameDateTime = new Date(year, month - 1, day, hour, minute, 0, 0);
      } else if (gameDate.includes('/')) {
        const [month, day, year] = gameDate.split('/');
        gameDateTime = new Date(year, month - 1, day, hour, minute, 0, 0);
      }
    } else {
      gameDateTime.setHours(hour);
      gameDateTime.setMinutes(minute);
      gameDateTime.setSeconds(0);
      gameDateTime.setMilliseconds(0);
    }
    
    if (gameStatus === 'Live' && now < gameDateTime) {
      const diffMs = gameDateTime - now;
      const diffMinutes = Math.ceil(diffMs / (1000 * 60));
      return <Badge bg="warning" className="countdown-badge">Starting in {diffMinutes} min</Badge>;
    }
    
    const isToday = gameDate && (gameDateTime.toDateString() === now.toDateString());
    
    if ((isToday && now > gameDateTime) || gameStatus === 'Live') {
      return <Badge bg="danger" className="live-badge"><span className="live-dot"></span>Live</Badge>;
    }
    
    const options = { hour: 'numeric', minute: 'numeric', hour12: true };
    return gameDateTime.toLocaleTimeString([], options);
  };

  // Sort batters by lineup position when available
  const sortBattersByLineup = (batters, team) => {
    const lineup = team === safeGame.away ? safeGame.awayStartingLineup : safeGame.homeStartingLineup;
    
    if (!lineup || lineup.length === 0) {
      // No lineup available, sort alphabetically by name
      return [...batters].sort((a, b) => a.batterName.localeCompare(b.batterName));
    }
    
    
    return [...batters].sort((a, b) => {
      const aId = a.batterId?.toString();
      const bId = b.batterId?.toString();
      
      const aPosition = aId ? lineup.indexOf(aId) : -1;
      const bPosition = bId ? lineup.indexOf(bId) : -1;
      
      
      // If both are in lineup, sort by lineup position
      if (aPosition !== -1 && bPosition !== -1) {
        return aPosition - bPosition;
      }
      
      // Players in lineup come before those not in lineup
      if (aPosition !== -1 && bPosition === -1) return -1;
      if (aPosition === -1 && bPosition !== -1) return 1;
      
      // If neither in lineup, sort alphabetically
      return a.batterName.localeCompare(b.batterName);
    });
  };

  const formatLastUpdated = (lastUpdate) => {
    if (!lastUpdate) return '';
    
    let date;
    if (typeof lastUpdate === 'string') {
      if (lastUpdate.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        if (!lastUpdate.includes('Z') && !lastUpdate.includes('+') && !lastUpdate.includes('-')) {
          date = new Date(lastUpdate + 'Z');
        } else {
          date = new Date(lastUpdate);
        }
      } else {
        date = new Date(lastUpdate);
      }
    } else if (typeof lastUpdate === 'number') {
      if (lastUpdate < 10000000000) {
        date = new Date(lastUpdate * 1000);
      } else {
        date = new Date(lastUpdate);
      }
    } else {
      date = new Date(lastUpdate);
    }
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date after parsing:', lastUpdate, 'Parsed:', date);
      return new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
      });
    }
    
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  };

  const formatStatValue = (value, format, row = null) => {
    if (value === null || value === undefined) return '-';

    // If at-bats is 0, show dashes for rate stats
    if (row && row.atBats === 0 && (format === 'decimal')) {
      return '-';
    }

    if (format === 'decimal') {
      const formatted = parseFloat(value).toFixed(3);
      // Remove leading zero for values less than 1
      if (value < 1) {
        return formatted.substring(1);
      }
      return formatted;
    } else if (format === 'percentage') {
      // Format as percentage with 1 decimal place
      return parseFloat(value).toFixed(1) + '%';
    } else if (format === 'velocity') {
      // Format exit velocity with 1 decimal place
      return parseFloat(value).toFixed(1);
    }

    return value.toString();
  };

  const getStatCellStyle = (statKey, value) => {
    const baseStyle = { ...statCellStyle };
    
    // Apply tier coloring for AVG, SLG, OPS, Hard Hit %, and Barrel % (handle both matchups, performance, and pitches naming)
    const statType = (statKey.includes('battingAvg') || statKey.endsWith('_avg')) ? 'battingAvg' :
                     (statKey.includes('slg') || statKey.endsWith('_slg')) ? 'slg' :
                     (statKey.includes('ops') || statKey.endsWith('_ops')) ? 'ops' :
                     (statKey.includes('hardHits') || statKey.includes('hhPercent')) ? 'hardHit' :
                     (statKey.includes('barrels') || statKey.includes('barrelPercent')) ? 'barrel' : null;
    
    if (statType === 'battingAvg') {
      if (value >= 0.350) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 0.320) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 0.280) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    } else if (statType === 'slg') {
      if (value >= 0.600) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 0.500) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 0.450) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    } else if (statType === 'ops') {
      if (value >= 1.000) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 0.900) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 0.800) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    } else if (statType === 'hardHit') {
      // Hard Hit % tiers: Best 45%+, Better 40-45%, Good 35-40%
      if (value >= 45) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 40) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 35) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    } else if (statType === 'barrel') {
      // Barrel % tiers: Best 20%+, Better 15-20%, Good 10-15%
      if (value >= 20) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 15) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 10) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    }
    
    return baseStyle;
  };


  const getMatchupInfo = (row) => {
    const hand = row.batterBats || row.battingHand;
    const batterHand = hand === 'Left' || hand === 'L' ? 'L' : hand === 'Right' || hand === 'R' ? 'R' : (hand || '?');
    
    // For performance and pitches analytics, use the opposing pitcher from game data
    let pitcherHand = row.pitcherThrows;
    if (analyticsType === 'performance' || analyticsType === 'pitches') {
      // For pitches mode, use the actual pitcher data from the endpoint if available
      if (analyticsType === 'pitches' && localData?.rawPitchersData) {
        const pitchersArray = localData.rawPitchersData;
        if (row.batterTeam === safeGame.away) {
          // Away team batter faces home pitcher
          const homePitcher = pitchersArray.find(p => p.pitcherTeam === safeGame.home);
          pitcherHand = homePitcher?.pitcherThrows === 'Left' ? 'L' : homePitcher?.pitcherThrows === 'Right' ? 'R' : homePitcher?.pitcherThrows || 'N/A';
        } else if (row.batterTeam === safeGame.home) {
          // Home team batter faces away pitcher
          const awayPitcher = pitchersArray.find(p => p.pitcherTeam === safeGame.away);
          pitcherHand = awayPitcher?.pitcherThrows === 'Left' ? 'L' : awayPitcher?.pitcherThrows === 'Right' ? 'R' : awayPitcher?.pitcherThrows || 'N/A';
        }
      } else {
        // For performance mode, use probable pitcher data
        if (row.batterTeam === safeGame.away) {
          // Away team batter faces home pitcher
          pitcherHand = safeGame.homeProbablePitcher?.throws === 'Left' ? 'L' : safeGame.homeProbablePitcher?.throws === 'Right' ? 'R' : safeGame.homeProbablePitcher?.throws || 'N/A';
        } else if (row.batterTeam === safeGame.home) {
          // Home team batter faces away pitcher
          pitcherHand = safeGame.awayProbablePitcher?.throws === 'Left' ? 'L' : safeGame.awayProbablePitcher?.throws === 'Right' ? 'R' : safeGame.awayProbablePitcher?.throws || 'N/A';
        }
      }
    } else {
      // For matchups analytics, use the existing data
      pitcherHand = row.pitcherThrows === 'Left' ? 'L' : row.pitcherThrows === 'Right' ? 'R' : row.pitcherThrows;
    }
    
    return `${batterHand} vs ${pitcherHand}`;
  };

  // Log what we're receiving
  
  // Get loading and last update from local data or passed props
  const isLoading = localLoading || analyticsLoadingGameIds?.has(safeGame.gameIDmlb);
  const lastUpdate = analyticsByGameId[safeGame.gameIDmlb]?.last_update;

  // Get current year's data for display
  let currentYearData = [];
  
  if (analyticsType === 'matchups' && localData) {
    if (typeof localData === 'object' && !Array.isArray(localData)) {
      // New format with years as keys
      currentYearData = localData[selectedYear] || [];
    } else {
      // Fallback for old format
      currentYearData = localData || [];
    }
  } else if (analyticsType === 'performance') {
    // For performance data, ensure it's an array
    currentYearData = Array.isArray(localData) ? localData : [];
  } else if (analyticsType === 'pitches') {
    // For pitches data, get the batters array from the structured data
    currentYearData = Array.isArray(localData?.batters) ? localData.batters : [];
  } else {
    // Fallback for other types
    currentYearData = Array.isArray(localData) ? localData : [];
  }

  let awayBatters = [];
  let homeBatters = [];
  let awayPitcherName = 'TBD';
  let homePitcherName = 'TBD';

  if (analyticsType === 'matchups') {
    
    // For BvP, show all matchup data, not just starting pitchers
    const allBatters = currentYearData || [];

    // Group all batters by team (no pitcher filtering for BvP view)
    awayBatters = allBatters.filter(row => row.batterTeam === safeGame.away);
    homeBatters = allBatters.filter(row => row.batterTeam === safeGame.home);
    
    // Sort by batting order when lineup is available
    awayBatters = sortBattersByLineup(awayBatters, safeGame.away);
    homeBatters = sortBattersByLineup(homeBatters, safeGame.home);
    
    
    // For display purposes, try to get starting pitcher names from probable pitchers
    awayPitcherName = safeGame.awayProbablePitcher?.fullName || 'TBD';
    homePitcherName = safeGame.homeProbablePitcher?.fullName || 'TBD';
  } else if (analyticsType === 'pitches') {
    // For pitches data, group by team and include pitcher rows
    // In splits mode, use the enhanced splits data with correct hand-specific stats
    const allBatters = (pitchesDisplayMode === 'splits' && localData?.splitsData) ? 
      [...(localData.splitsData.awayBattersWithSplits || []), ...(localData.splitsData.homeBattersWithSplits || [])] :
      (localData?.batters || []);
    const pitcherRows = localData?.pitchers || [];
    
    
    if (Array.isArray(allBatters) || (pitchesDisplayMode === 'splits' && localData?.splitsData)) {
      const awayBattersData = (pitchesDisplayMode === 'splits' && localData?.splitsData?.awayBattersWithSplits) ?
        localData.splitsData.awayBattersWithSplits :
        allBatters.filter(row => row.batterTeam === safeGame.away);
      const homeBattersData = (pitchesDisplayMode === 'splits' && localData?.splitsData?.homeBattersWithSplits) ?
        localData.splitsData.homeBattersWithSplits :
        allBatters.filter(row => row.batterTeam === safeGame.home);
      
      // Sort by batting order when lineup is available
      const sortedAwayBatters = sortBattersByLineup(awayBattersData, safeGame.away);
      const sortedHomeBatters = sortBattersByLineup(homeBattersData, safeGame.home);
      
      // Add OPPOSING pitcher rows at the beginning of each team's data
      // Away batters face the HOME pitcher, home batters face the AWAY pitcher
      const homePitcherRows = pitcherRows.filter(p => p.batterTeam === safeGame.home);
      const awayPitcherRows = pitcherRows.filter(p => p.batterTeam === safeGame.away);
      
      
      // Create a buffer row (separator) between pitcher and batters
      const bufferRow = {
        batterId: 'buffer',
        batterName: '',
        batterTeam: '',
        batterBats: '',
        isBuffer: true
      };
      
      // In splits mode, separate batters by handedness
      if (pitchesDisplayMode === 'splits') {
        // Separate batters by handedness
        const awayRHBatters = sortedAwayBatters.filter(b => b.battingHand === 'Right' || b.batterBats === 'Right');
        const awayLHBatters = sortedAwayBatters.filter(b => b.battingHand === 'Left' || b.batterBats === 'Left');
        const homeRHBatters = sortedHomeBatters.filter(b => b.battingHand === 'Right' || b.batterBats === 'Right');
        const homeLHBatters = sortedHomeBatters.filter(b => b.battingHand === 'Left' || b.batterBats === 'Left');
        
        // Create labels for handedness groups
        const rhLabel = {
          batterId: 'rh_label',
          batterName: 'RH',
          playerName: 'RH',
          isSplitsLabel: true,
          isPitcher: false
        };
        
        const lhLabel = {
          batterId: 'lh_label',
          batterName: 'LH',
          playerName: 'LH',
          isSplitsLabel: true,
          isPitcher: false
        };
        
        // Build arrays with pitcher rows and batters grouped by handedness (no labels)
        const awayBattersSplits = [...awayRHBatters, ...awayLHBatters];
        const homeBattersSplits = [...homeRHBatters, ...homeLHBatters];
        
        awayBatters = homePitcherRows.length > 0 ? [...homePitcherRows, bufferRow, ...awayBattersSplits] : awayBattersSplits;
        homeBatters = awayPitcherRows.length > 0 ? [...awayPitcherRows, bufferRow, ...homeBattersSplits] : homeBattersSplits;
      } else {
        // Default mode - show all batters together
        awayBatters = homePitcherRows.length > 0 ? [...homePitcherRows, bufferRow, ...sortedAwayBatters] : sortedAwayBatters;
        homeBatters = awayPitcherRows.length > 0 ? [...awayPitcherRows, bufferRow, ...sortedHomeBatters] : sortedHomeBatters;
      }
    } else {
      console.warn('Pitches data is not an array:', allBatters);
      awayBatters = [];
      homeBatters = [];
    }
  } else {
    // For performance data, group by team but no pitcher-specific filtering
    const allBatters = currentYearData || [];
    
    // Ensure allBatters is an array before filtering
    if (Array.isArray(allBatters)) {
      awayBatters = allBatters.filter(row => row.batterTeam === safeGame.away);
      homeBatters = allBatters.filter(row => row.batterTeam === safeGame.home);
    } else {
      console.warn('Performance data is not an array:', allBatters);
      awayBatters = [];
      homeBatters = [];
    }
    
    // Sort by batting order when lineup is available
    awayBatters = sortBattersByLineup(awayBatters, safeGame.away);
    homeBatters = sortBattersByLineup(homeBatters, safeGame.home);
  }


  if (!localData || 
      (Array.isArray(localData) && localData.length === 0) || 
      (analyticsType === 'matchups' && (!currentYearData || currentYearData.length === 0)) ||
      (analyticsType === 'pitches' && (!localData.batters || localData.batters.length === 0) && (!localData.pitchers || localData.pitchers.length === 0)) ||
      isLoading) {
    const skeletonRows = [1, 2, 3, 4, 5];
    
    return (
      <div className="table-group">
        <div className="game-header-container">
          <img
            src={getTeamLogoUrl(safeGame.away)}
            alt={safeGame.away}
            style={{ width: '32px', height: '32px', marginRight: '8px' }}
          />
          @
          <img
            src={getTeamLogoUrl(safeGame.home)}
            alt={safeGame.home}
            style={{ width: '32px', height: '32px', margin: '0 8px' }}
          />
        </div>

        {/* Skeleton Loading */}
        {!isMobile && (
          <div className="table-container">
            <Table bordered size="sm" className="text-center">
              <thead>
                <tr>
                  <th colSpan={visibleStats.length + 1} className="bg-light">
                    {formatTime(gameTime, gameStatus)} - {isLoading ? 'Loading Analytics...' : 'No Analytics Data'}
                  </th>
                </tr>
                <tr>
                  <th className="text-start" style={{ minWidth: '200px' }}>Batter vs Pitcher</th>
                  {visibleStats.map(stat => (
                    <th key={`skeleton-${stat.key}`} style={statCellStyle} title={stat.name}>
                      {stat.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {skeletonRows.map(i => (
                  <tr key={`skeleton-${i}`}>
                    <td className="player-name-cell">
                      <div className="skeleton-text"></div>
                    </td>
                    {visibleStats.map(stat => (
                      <td key={`${stat.key}-${i}`} className="odds-cell" style={statCellStyle}>
                        <div className="skeleton-odds"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {isMobile && (
          <div className="table-container">
            <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th className="bg-light" style={{ width: `${mobileNameWidth}%` }}>{formatTime(gameTime, gameStatus)}</th>
                  <th colSpan={visibleStats.length} className="bg-light" style={{ width: `${mobileStatWidth * visibleStats.length}%` }}>
                    {isLoading ? 'Loading Analytics...' : 'No Analytics Data'}
                  </th>
                </tr>
                <tr>
                  <th className="text-start" style={{ width: `${mobileNameWidth}%` }}>Batter vs Pitcher</th>
                  {visibleStats.map(stat => (
                    <th key={`skeleton-${stat.key}`} style={{ width: `${mobileStatWidth}%`, textAlign: 'center' }} title={stat.name}>
                      {stat.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {skeletonRows.map(i => (
                  <tr key={`skeleton-${i}`}>
                    <td className="player-name-cell" style={{ width: `${mobileNameWidth}%`, fontSize: '0.8rem' }}>
                      <div className="skeleton-text"></div>
                    </td>
                    {visibleStats.map(stat => (
                      <td key={`skeleton-${stat.key}-${i}`} className="odds-cell" style={{ width: `${mobileStatWidth}%`, textAlign: 'center', fontSize: '0.75rem' }}>
                        <div className="skeleton-odds"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        
        {lastUpdate && (
          <div className="last-updated" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1, textAlign: 'left', paddingLeft: '10px' }}>
              Historical Matchup Data
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              Last updated: {formatLastUpdated(lastUpdate)} ET
            </div>
          </div>
        )}
      </div>
    );
  }

  // Use mobile component for mobile devices
  if (isMobile) {
    return (
      <>
        <MobileAnalyticsDataTable
          analyticsByGameId={analyticsByGameId}
          analyticsLoadingGameIds={analyticsLoadingGameIds}
          gameTime={gameTime}
          gameStatus={gameStatus}
          gameDate={gameDate}
          lastUpdate={null}
          game={game}
          formatPlayerName={formatPlayerName}
          getTeamLogoUrl={getTeamLogoUrl}
          getBookLogoUrl={null}
          selectedMarket="h2h"
          selectedMarketName=""
          bookmakers={[]}
          isLoading={false}
          availableMarkets={[]}
          onMarketSelection={() => {}}
          showTiers={false}
          onTierToggle={() => {}}
          masterTierFilter=""
          onPlayerClick={handlePlayerClick}
          onOddsClick={() => {}}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
        />

        {/* Player Modal for mobile */}
        <PlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          gameId={playerModalData.gameId}
          playerId={playerModalData.playerId}
          playerName={playerModalData.playerName}
          market={playerModalData.market}
          awayTeam={playerModalData.awayTeam}
          homeTeam={playerModalData.homeTeam}
          getTeamLogoUrl={getTeamLogoUrl}
          getBookLogoUrl={() => null}
          sport="mlb"
        />
      </>
    );
  }

  return (
    <div className="table-group">
      {/* Game Header */}
      <div className="game-header-container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', position: 'relative' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 'bold', position: 'absolute', left: '10px' }}>
            {formatTime(gameTime, gameStatus)}
          </span>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <img
              src={getTeamLogoUrl(safeGame.away)}
              alt={safeGame.away}
              style={{ width: '32px', height: '32px', marginRight: '8px' }}
            />
            @
            <img
              src={getTeamLogoUrl(safeGame.home)}
              alt={safeGame.home}
              style={{ width: '32px', height: '32px', margin: '0 8px' }}
            />
          </div>
          
          {/* Collapse toggle for mobile */}
          {isMobile && (
            <span
              onClick={() => onCollapseToggle(!isCollapsed)}
              style={{ 
                position: 'absolute',
                right: 0,
                cursor: 'pointer', 
                color: '#6c757d', 
                fontSize: '14px',
                userSelect: 'none',
                padding: '4px 8px'
              }}>
              {isCollapsed ? '▼' : '▲'}
            </span>
          )}
        </div>
      </div>
      
      {/* Analytics type toggle buttons */}
      {!isMobile && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${analyticsType === 'matchups' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnalyticsType('matchups')}
              style={{ fontSize: '11px' }}
            >
              BvP
            </button>
            <button
              type="button"
              className={`btn ${analyticsType === 'performance' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnalyticsType('performance')}
              style={{ fontSize: '11px' }}
            >
              Last X
            </button>
            <button
              type="button"
              className={`btn ${analyticsType === 'pitches' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnalyticsType('pitches')}
              style={{ fontSize: '11px' }}
            >
              Pitches
            </button>
          </div>
        </div>
      )}
      
      {/* Year selection for BvP */}
      {analyticsType === 'matchups' && !isMobile && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${selectedYear === 'alltime' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedYear('alltime')}
              style={{ fontSize: '11px' }}
            >
              Combined
            </button>
            <button
              type="button"
              className={`btn ${selectedYear === '2025' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedYear('2025')}
              style={{ fontSize: '11px' }}
            >
              2025
            </button>
            <button
              type="button"
              className={`btn ${selectedYear === '2024' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedYear('2024')}
              style={{ fontSize: '11px' }}
            >
              2024
            </button>
          </div>
        </div>
      )}
      
      {/* Performance view toggle buttons for Last X */}
      {analyticsType === 'performance' && !isMobile && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${performanceView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('all')}
              style={{ fontSize: '11px' }}
            >
              All
            </button>
            <button
              type="button"
              className={`btn ${performanceView === 'last7' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('last7')}
              style={{ fontSize: '11px' }}
            >
              Last 7
            </button>
            <button
              type="button"
              className={`btn ${performanceView === 'last14' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('last14')}
              style={{ fontSize: '11px' }}
            >
              Last 14
            </button>
            <button
              type="button"
              className={`btn ${performanceView === 'last30' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('last30')}
              style={{ fontSize: '11px' }}
            >
              Last 30
            </button>
          </div>
          
          {/* Pitcher Hand Filter - Only show when in performance mode */}
          {analyticsType === 'performance' && (
            <div className="btn-group btn-group-sm ms-3" role="group">
              <button
                type="button"
                className={`btn ${pitcherHandFilter === 'vsAll' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPitcherHandFilter('vsAll')}
                style={{ fontSize: '11px' }}
              >
                Both
              </button>
              <button
                type="button"
                className={`btn ${pitcherHandFilter === 'vsRHP' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPitcherHandFilter('vsRHP')}
                style={{ fontSize: '11px' }}
              >
                vs RHP
              </button>
              <button
                type="button"
                className={`btn ${pitcherHandFilter === 'vsLHP' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPitcherHandFilter('vsLHP')}
                style={{ fontSize: '11px' }}
              >
                vs LHP
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Pitches view toggle buttons */}
      {analyticsType === 'pitches' && !isMobile && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          {/* All/Splits Filter for pitches */}
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${pitchesDisplayMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesDisplayMode('all')}
              style={{ fontSize: '11px' }}
            >
              All
            </button>
            <button
              type="button"
              className={`btn ${pitchesDisplayMode === 'splits' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesDisplayMode('splits')}
              style={{ fontSize: '11px' }}
            >
              Splits
            </button>
          </div>
          
          {/* Main/Advanced Toggle */}
          <div className="btn-group btn-group-sm ms-3" role="group">
            <button
              type="button"
              className={`btn ${pitchesView === 'main' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesView('main')}
              style={{ fontSize: '11px' }}
            >
              Main
            </button>
            <button
              type="button"
              className={`btn ${pitchesView === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesView('advanced')}
              style={{ fontSize: '11px' }}
            >
              Advanced
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile analytics type toggle */}
      {isMobile && !isCollapsed && (
        <div className="d-flex justify-content-center mt-2 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${analyticsType === 'matchups' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnalyticsType('matchups')}
              style={{ fontSize: '10px' }}
            >
              BvP
            </button>
            <button
              type="button"
              className={`btn ${analyticsType === 'performance' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnalyticsType('performance')}
              style={{ fontSize: '10px' }}
            >
              Last X
            </button>
            <button
              type="button"
              className={`btn ${analyticsType === 'pitches' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnalyticsType('pitches')}
              style={{ fontSize: '10px' }}
            >
              Pitches
            </button>
          </div>
        </div>
      )}
      
      {/* Year selection for BvP on mobile */}
      {analyticsType === 'matchups' && isMobile && !isCollapsed && (
        <div className="d-flex justify-content-center mt-2 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${selectedYear === 'alltime' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedYear('alltime')}
              style={{ fontSize: '9px' }}
            >
              Comb
            </button>
            <button
              type="button"
              className={`btn ${selectedYear === '2025' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedYear('2025')}
              style={{ fontSize: '9px' }}
            >
              2025
            </button>
            <button
              type="button"
              className={`btn ${selectedYear === '2024' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedYear('2024')}
              style={{ fontSize: '9px' }}
            >
              2024
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile performance toggle for Last X */}
      {analyticsType === 'performance' && isMobile && !isCollapsed && (
        <div className="d-flex justify-content-center mt-2 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${performanceView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('all')}
              style={{ fontSize: '10px' }}
            >
              All
            </button>
            <button
              type="button"
              className={`btn ${performanceView === 'last7' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('last7')}
              style={{ fontSize: '10px' }}
            >
              7d
            </button>
            <button
              type="button"
              className={`btn ${performanceView === 'last14' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('last14')}
              style={{ fontSize: '10px' }}
            >
              14d
            </button>
            <button
              type="button"
              className={`btn ${performanceView === 'last30' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPerformanceView('last30')}
              style={{ fontSize: '10px' }}
            >
              30d
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile Pitcher Hand Filter - Only show when in performance mode */}
      {analyticsType === 'performance' && isMobile && !isCollapsed && (
        <div className="d-flex justify-content-center mt-1 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${pitcherHandFilter === 'vsAll' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitcherHandFilter('vsAll')}
              style={{ fontSize: '10px' }}
            >
              Both
            </button>
            <button
              type="button"
              className={`btn ${pitcherHandFilter === 'vsRHP' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitcherHandFilter('vsRHP')}
              style={{ fontSize: '10px' }}
            >
              vs RHP
            </button>
            <button
              type="button"
              className={`btn ${pitcherHandFilter === 'vsLHP' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitcherHandFilter('vsLHP')}
              style={{ fontSize: '10px' }}
            >
              vs LHP
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile Pitches Toggle for All/Splits */}
      {analyticsType === 'pitches' && isMobile && !isCollapsed && (
        <div className="d-flex justify-content-center mt-2 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${pitchesDisplayMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesDisplayMode('all')}
              style={{ fontSize: '9px' }}
            >
              All
            </button>
            <button
              type="button"
              className={`btn ${pitchesDisplayMode === 'splits' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesDisplayMode('splits')}
              style={{ fontSize: '9px' }}
            >
              Splits
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile Pitches Main/Advanced Toggle */}
      {analyticsType === 'pitches' && isMobile && !isCollapsed && (
        <div className="d-flex justify-content-center mt-1 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${pitchesView === 'main' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesView('main')}
              style={{ fontSize: '9px' }}
            >
              Main
            </button>
            <button
              type="button"
              className={`btn ${pitchesView === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPitchesView('advanced')}
              style={{ fontSize: '9px' }}
            >
              Advanced
            </button>
          </div>
        </div>
      )}


      {(!isMobile || !isCollapsed) && (
        <div>
          {/* Desktop: Two tables layout - one per team (or 4 tables in splits mode) */}
          {!isMobile && analyticsType === 'pitches' && pitchesDisplayMode === 'splits' ? (
            <SplitsView 
              localData={localData}
              safeGame={safeGame}
              awayBatters={awayBatters}
              homeBatters={homeBatters}
              splitsData={localData?.splitsData}
              formatStatValue={formatStatValue}
              getTeamLogoUrl={getTeamLogoUrl}
              getStatCellStyle={getStatCellStyle}
              pitchesView={pitchesView}
            />
          ) : !isMobile ? (
            <DesktopTablesView 
              analyticsType={analyticsType}
              groupedColumns={groupedColumns}
              awayGroupedColumns={awayGroupedColumns}
              homeGroupedColumns={homeGroupedColumns}
              performanceView={performanceView}
              awayVisibleStats={awayVisibleStats}
              homeVisibleStats={homeVisibleStats}
              awayBatters={awayBatters}
              homeBatters={homeBatters}
              safeGame={safeGame}
              getTeamLogoUrl={getTeamLogoUrl}
              formatPlayerName={formatPlayerName}
              getMatchupInfo={getMatchupInfo}
              getStatCellStyle={getStatCellStyle}
              formatStatValue={formatStatValue}
            />
          ) : null}
        </div>
      )}

          {/* Mobile: Two tables with swipe navigation */}

          {isMobile && (
            <div {...swipeHandlers} className="d-flex flex-column" style={{ gap: '15px' }}>
              {/* Mobile swipe dots indicator */}
              {totalStatPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mb-2" style={{ gap: '6px' }}>
                  {Array.from({ length: totalStatPages }, (_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (!isTransitioning) {
                          setIsTransitioning(true);
                          setMobileStatIndex(index);
                          setTimeout(() => setIsTransitioning(false), 300);
                        }
                      }}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        border: 'none',
                        background: index === mobileStatIndex ? '#007bff' : '#dee2e6',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        padding: '0',
                        margin: '0'
                      }}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>
              )}
              
              {/* Away Team Table - Mobile */}
              <div className="table-container">
                <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ display: 'none' }}>
                    </tr>
                    <tr>
                      <th className="text-start player-name-cell" style={{ width: `${mobileNameWidth}%`, fontSize: '0.75rem', backgroundImage: `url(${getTeamLogoUrl(safeGame.home)})`, color: 'yellow', textTransform: 'none' }}>
                        {analyticsType === 'matchups' ?
                          renderMlbPitcherHeader(safeGame, true) :
                         analyticsType === 'performance' ? (
                           performanceView === 'all' ? 'All' :
                           performanceView === 'last7' ? 'L7' :
                           performanceView === 'last14' ? 'L14' : 'L30'
                         ) : 'Pitches'}
                      </th>
                      {awayVisibleStats.map(stat => {
                        // For pitches mode, show both pitch name and stat type
                        let displayLabel = stat.label;
                        if (analyticsType === 'pitches' && stat.group) {
                          // Extract pitch name from group (e.g., "4-Seam FB (34.2%)" -> "4-Seam")
                          const pitchName = stat.group.split(' (')[0];
                          // Shorten pitch name for mobile
                          const shortPitch = pitchName
                            .replace('Four-Seam FB', '4-Seam')
                            .replace('Two-Seam FB', '2-Seam')
                            .replace(' Fastball', '')
                            .replace(' FB', '');
                          // Since we show 3 stats per pitch, add stat type as subtitle
                          displayLabel = (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <div style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>{shortPitch}</div>
                              <div style={{ fontSize: '0.65rem' }}>{stat.label}</div>
                            </div>
                          );
                        }
                        return (
                          <th key={stat.key} style={{ width: `${mobileStatWidth}%`, textAlign: 'center', padding: '4px 2px' }} title={stat.name}>
                            {displayLabel}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {awayBatters.length === 0 && (
                      <tr>
                        <td colSpan={visibleStats.length + 1} className="text-center text-muted">
                          No away batters data
                        </td>
                      </tr>
                    )}
                    {awayBatters.map((row, index) => {
                      if (row.isBuffer) {
                        return (
                          <tr key={`buffer-${index}`} style={{ height: '5px', backgroundColor: '#000' }}>
                            <td colSpan={visibleStats.length + 1} style={{ backgroundColor: '#000', padding: '0', height: '5px' }}></td>
                          </tr>
                        );
                      }
                      
                      return (
                        <tr key={`${row.batterId}-${row.pitcherId}-${index}`} className={row.isPitcher ? 'pitcher-row' : ''} style={row.isPitcher ? { backgroundColor: 'rgb(26,26,26)' } : {}}>
                          <td 
                          className="player-name-cell"
                          style={{ 
                            backgroundImage: `url(${getTeamLogoUrl(row.batterTeam)})`,
                            width: `${mobileNameWidth}%`,
                            fontSize: '0.8rem',
                            backgroundColor: row.isPitcher ? 'rgb(26,26,26)' : 'transparent',
                            ...(row.isPitcher ? { color: '#1a365d' } : {})
                          }}
                        >
                          <span>{row.isPitcher ? `vs ${row.batterName}` : (formatPlayerName ? formatPlayerName(row.batterName, isMobile, true, safeGame.sport) : row.batterName)}</span>
                          {!row.isPitcher && (
                            <span style={{ fontSize: '0.65rem', color: '#6c757d', marginLeft: '4px' }}>
                              {getMatchupInfo(row)}
                            </span>
                          )}
                        </td>
                        {awayVisibleStats.map(stat => (
                          <td key={stat.key} style={{ 
                            ...getStatCellStyle(stat.key, row[stat.key]), 
                            width: `${mobileStatWidth}%`, 
                            textAlign: 'center', 
                            fontSize: '0.7rem',
                            ...(row.isPitcher && !getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? { backgroundColor: 'rgb(26,26,26)', color: 'white' } : {}),
                            ...(row.isPitcher && getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? {} : {})
                          }}>
                            {formatStatValue(row[stat.key], stat.format)}
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              
              {/* Home Team Table - Mobile */}
              <div className="table-container">
                <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ display: 'none' }}>
                    </tr>
                    <tr>
                      <th className="text-start player-name-cell" style={{ width: `${mobileNameWidth}%`, fontSize: '0.75rem', backgroundImage: `url(${getTeamLogoUrl(safeGame.away)})`, color: 'yellow', textTransform: 'none' }}>
                        {analyticsType === 'matchups' ?
                          renderMlbPitcherHeader(safeGame, false) :
                         analyticsType === 'performance' ? (
                           performanceView === 'all' ? 'All' :
                           performanceView === 'last7' ? 'L7' :
                           performanceView === 'last14' ? 'L14' : 'L30'
                         ) : 'Pitches'}
                      </th>
                      {homeVisibleStats.map(stat => {
                        // For pitches mode, show the pitch name from the group
                        let displayLabel = stat.label;
                        if (analyticsType === 'pitches' && stat.group) {
                          // Extract pitch name from group (e.g., "4-Seam FB (34.2%)" -> "4-Seam")
                          const pitchName = stat.group.split(' (')[0];
                          // Further shorten for mobile if needed
                          displayLabel = pitchName.replace(' FB', '').replace(' Fastball', '');
                        }
                        return (
                          <th key={stat.key} style={{ width: `${mobileStatWidth}%`, textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                            {displayLabel}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {homeBatters.length === 0 && (
                      <tr>
                        <td colSpan={homeVisibleStats.length + 1} className="text-center text-muted">
                          No home batters data
                        </td>
                      </tr>
                    )}
                    {homeBatters.map((row, index) => {
                      if (row.isBuffer) {
                        return (
                          <tr key={`buffer-${index}`} style={{ height: '5px', backgroundColor: '#000' }}>
                            <td colSpan={homeVisibleStats.length + 1} style={{ backgroundColor: '#000', padding: '0', height: '5px' }}></td>
                          </tr>
                        );
                      }
                      
                      return (
                        <tr key={`${row.batterId}-${row.pitcherId}-${index}`} className={row.isPitcher ? 'pitcher-row' : ''} style={row.isPitcher ? { backgroundColor: 'rgb(26,26,26)' } : {}}>
                          <td 
                          className="player-name-cell"
                          style={{ 
                            backgroundImage: `url(${getTeamLogoUrl(row.batterTeam)})`,
                            width: `${mobileNameWidth}%`,
                            fontSize: '0.8rem',
                            backgroundColor: row.isPitcher ? 'rgb(26,26,26)' : 'transparent',
                            ...(row.isPitcher ? { color: '#1a365d' } : {})
                          }}
                        >
                          <span>{row.isPitcher ? `vs ${row.batterName}` : (formatPlayerName ? formatPlayerName(row.batterName, isMobile, true, safeGame.sport) : row.batterName)}</span>
                          {!row.isPitcher && (
                            <span style={{ fontSize: '0.65rem', color: '#6c757d', marginLeft: '4px' }}>
                              {getMatchupInfo(row)}
                            </span>
                          )}
                        </td>
                        {homeVisibleStats.map(stat => (
                          <td key={stat.key} style={{ 
                            ...getStatCellStyle(stat.key, row[stat.key]), 
                            width: `${mobileStatWidth}%`, 
                            textAlign: 'center', 
                            fontSize: '0.7rem',
                            ...(row.isPitcher && !getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? { backgroundColor: 'rgb(26,26,26)', color: 'white' } : {}),
                            ...(row.isPitcher && getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? {} : {})
                          }}>
                            {formatStatValue(row[stat.key], stat.format)}
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
      
      {lastUpdate && (!isMobile || !isCollapsed) && (
        <div className="last-updated" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, textAlign: 'left', paddingLeft: '10px' }}>
            Historical Matchup Data
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            Last updated: {formatLastUpdated(lastUpdate)} ET
          </div>
        </div>
      )}

      {/* Player Modal */}
      <PlayerModal
        show={showPlayerModal}
        onHide={handleClosePlayerModal}
        gameId={playerModalData.gameId}
        playerId={playerModalData.playerId}
        playerName={playerModalData.playerName}
        market={playerModalData.market}
        awayTeam={playerModalData.awayTeam}
        homeTeam={playerModalData.homeTeam}
        getTeamLogoUrl={getTeamLogoUrl}
        getBookLogoUrl={() => null}
        sport="mlb"
      />
    </div>
  );
};

export default AnalyticsDataTable;